// Atlas API client — wrapper sobre /api/atlas usando o email do usuário
// como identidade (padrão do projeto).

const API_BASE = (import.meta as any).env?.VITE_API_BASE_URL || '';

export interface AtlasUser {
  email?: string;
  empresaId?: string;
}

function withUserParam(url: string, user: AtlasUser): string {
  if (!user?.email) return url;
  const sep = url.includes('?') ? '&' : '?';
  return `${url}${sep}userEmail=${encodeURIComponent(user.email)}`;
}

async function handle(res: Response): Promise<any> {
  if (res.status === 204) return null;
  const text = await res.text();
  let data: any = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const msg = (data && data.erro) || (data && data.error) || `HTTP ${res.status}`;
    const err: any = new Error(msg);
    err.status = res.status;
    err.body = data;
    throw err;
  }
  return data;
}

function jsonReq(method: string, url: string, user: AtlasUser, body?: any): Promise<Response> {
  return fetch(withUserParam(url, user), {
    method,
    headers: { 'Content-Type': 'application/json' },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
}

export const atlasApi = {
  async health() {
    const r = await fetch(`${API_BASE}/api/atlas/health`);
    return handle(r);
  },

  async syncStatus(user: AtlasUser) {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/sync/status`, user));
    return handle(r);
  },

  async dispararSync(user: AtlasUser, empresaId?: string) {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/sync`, user), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ empresaId }),
    });
    return handle(r);
  },

  async listarColaboradores(user: AtlasUser, params: {
    status?: string; departamento?: string | number; filial?: string | number; busca?: string;
    page?: number; per_page?: number;
  } = {}) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.departamento !== undefined) qs.set('departamento', String(params.departamento));
    if (params.filial !== undefined && params.filial !== '') qs.set('filial', String(params.filial));
    if (params.busca) qs.set('busca', params.busca);
    if (params.page) qs.set('page', String(params.page));
    if (params.per_page) qs.set('per_page', String(params.per_page));
    const url = `${API_BASE}/api/atlas/colaboradores?${qs.toString()}`;
    const r = await fetch(withUserParam(url, user));
    return handle(r);
  },

  /** Filiais (funcempe do GIX) com contagem — alimenta o filtro da lista. */
  async listarFiliais(user: AtlasUser, params: { status?: string } = {}): Promise<{ itens: Filial[]; total: number }> {
    const qs = params.status ? `?status=${encodeURIComponent(params.status)}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/colaboradores/filiais${qs}`, user)));
  },

  async getColaborador(user: AtlasUser, funccodi: number) {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/colaboradores/${funccodi}`, user));
    return handle(r);
  },

  async editarColaborador(user: AtlasUser, funccodi: number, body: Record<string, any>) {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/colaboradores/${funccodi}`, user), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return handle(r);
  },

  // ===== Dashboard RH / People Analytics (módulo #10) =====
  async getDashboardResumo(user: AtlasUser, params: { cc?: string; departamento?: string } = {}): Promise<DashboardResumo> {
    const qs = new URLSearchParams();
    if (params.cc) qs.set('cc', params.cc);
    if (params.departamento) qs.set('departamento', params.departamento);
    const sep = qs.toString() ? `?${qs.toString()}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/dashboard/resumo${sep}`, user)));
  },
  async getTurnover(user: AtlasUser, meses = 12, params: { cc?: string; departamento?: string } = {}): Promise<{ meses: TurnoverMes[] }> {
    const qs = new URLSearchParams();
    qs.set('meses', String(meses));
    if (params.cc) qs.set('cc', params.cc);
    if (params.departamento) qs.set('departamento', params.departamento);
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/dashboard/turnover?${qs.toString()}`, user)));
  },
  async getHeadcountHistorico(user: AtlasUser, meses = 12, params: { cc?: string; departamento?: string } = {}): Promise<{ meses: HeadcountMes[] }> {
    const qs = new URLSearchParams();
    qs.set('meses', String(meses));
    if (params.cc) qs.set('cc', params.cc);
    if (params.departamento) qs.set('departamento', params.departamento);
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/dashboard/headcount-historico?${qs.toString()}`, user)));
  },

  // ===== Cadastros enriquecidos (módulo #22) =====
  async putDependentes(user: AtlasUser, funccodi: number, itens: Dependente[]) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/colaboradores/${funccodi}/dependentes`, user, itens));
  },
  async putFamilia(user: AtlasUser, funccodi: number, familia: Familia) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/colaboradores/${funccodi}/familia`, user, familia));
  },
  async putUniforme(user: AtlasUser, funccodi: number, uniforme: Uniforme) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/colaboradores/${funccodi}/uniforme`, user, uniforme));
  },
  async putContato(user: AtlasUser, funccodi: number, contato: Contato) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/colaboradores/${funccodi}/contato`, user, contato));
  },
  async putEndereco(user: AtlasUser, funccodi: number, endereco: Endereco) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/colaboradores/${funccodi}/endereco`, user, endereco));
  },
  async putFormacao(user: AtlasUser, funccodi: number, itens: Formacao[]) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/colaboradores/${funccodi}/formacao`, user, itens));
  },
  async putIdiomas(user: AtlasUser, funccodi: number, itens: Idioma[]) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/colaboradores/${funccodi}/idiomas`, user, itens));
  },
  async putCertificacoes(user: AtlasUser, funccodi: number, itens: Certificacao[]) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/colaboradores/${funccodi}/certificacoes`, user, itens));
  },
  async putTags(user: AtlasUser, funccodi: number, tags: string[]) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/colaboradores/${funccodi}/tags`, user, tags));
  },
  async putDocumentosPessoais(user: AtlasUser, funccodi: number, docs: DocumentosPessoais) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/colaboradores/${funccodi}/documentos-pessoais`, user, docs));
  },
  async putHistoricoCargos(user: AtlasUser, funccodi: number, itens: HistoricoCargo[]) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/colaboradores/${funccodi}/historico-cargos`, user, itens));
  },
  async buscarCep(user: AtlasUser, cep: string): Promise<Endereco> {
    const limpo = cep.replace(/\D/g, '');
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/utils/cep/${limpo}`, user)));
  },

  async criarColaboradorManual(user: AtlasUser, body: NovoColaboradorManual) {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/colaboradores`, user), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return handle(r);
  },

  async organograma(user: AtlasUser) {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/organograma`, user));
    return handle(r);
  },

  async hierarquia(user: AtlasUser): Promise<HierarquiaResp> {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/hierarquia`, user));
    return handle(r);
  },

  async hierarquiaAutoSugerir(user: AtlasUser, opts: { aplicar?: boolean; forcar?: boolean } = {}): Promise<HierarquiaAutoSugerirResp> {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/hierarquia/auto-sugerir`, user), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(opts),
    });
    return handle(r);
  },

  // Permissões (admin)
  async listarPermissoes(user: AtlasUser, params: { busca?: string; nivel?: string } = {}) {
    const qs = new URLSearchParams();
    if (params.busca) qs.set('busca', params.busca);
    if (params.nivel) qs.set('nivel', params.nivel);
    const sep = qs.toString() ? `?${qs.toString()}` : '';
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/permissoes${sep}`, user));
    return handle(r);
  },

  async atualizarPermissao(user: AtlasUser, userId: string, nivel: string | null) {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/permissoes/${userId}`, user), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ nivel }),
    });
    return handle(r);
  },

  // Foto (endpoint separado para evitar payload pesado)
  async uploadFoto(user: AtlasUser, funccodi: number, fotoBase64: string) {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/colaboradores/${funccodi}/foto`, user), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ foto_base64: fotoBase64 }),
    });
    return handle(r);
  },

  /** URL da imagem para usar em <img src=...>. Backend resolve upload manual ou GIX. */
  fotoUrl(user: AtlasUser, funccodi: number, bust?: string | number): string {
    let url = withUserParam(`${API_BASE}/api/atlas/colaboradores/${funccodi}/foto`, user);
    if (bust !== undefined && bust !== null && bust !== '') {
      url += (url.includes('?') ? '&' : '?') + `v=${encodeURIComponent(String(bust))}`;
    }
    return url;
  },

  // DISC
  async discQuestionario(user: AtlasUser): Promise<{ grupos: DiscGrupo[] }> {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/disc/questionario`, user));
    return handle(r);
  },

  async discSalvarRespostas(
    user: AtlasUser,
    body: { funccodi?: number; respostas: DiscResposta[] },
  ): Promise<{ ok: true; perfil: DiscPerfil }> {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/disc/respostas`, user), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    return handle(r);
  },

  async discColaborador(user: AtlasUser, funccodi: number): Promise<DiscColaborador> {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/disc/colaborador/${funccodi}`, user));
    return handle(r);
  },

  async discMeu(user: AtlasUser): Promise<DiscColaborador & { funccodi: number | null }> {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/disc/meu`, user));
    return handle(r);
  },

  /** Relatório DISC completo em PDF (gerado no servidor). */
  async discPdf(user: AtlasUser, funccodi?: number): Promise<{ blob: Blob; nomeArquivo: string }> {
    const url = funccodi !== undefined
      ? `${API_BASE}/api/atlas/disc/colaborador/${funccodi}/pdf`
      : `${API_BASE}/api/atlas/disc/meu/pdf`;
    const r = await fetch(withUserParam(url, user));
    if (!r.ok) return handle(r);   // handle() lança com a mensagem do backend
    const cd = r.headers.get('Content-Disposition') || '';
    const m = /filename="([^"]+)"/.exec(cd);
    return { blob: await r.blob(), nomeArquivo: m ? m[1] : 'perfil-disc.pdf' };
  },

  async discTime(user: AtlasUser, departamento?: number | string): Promise<DiscTime> {
    const qs = departamento !== undefined && departamento !== '' ? `?departamento=${encodeURIComponent(String(departamento))}` : '';
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/disc/time${qs}`, user));
    return handle(r);
  },

  // ===== DISC por link (RH solicita, colaborador responde sem login) =====
  async discAcesso(user: AtlasUser, funccodi?: number): Promise<DiscAcesso> {
    const qs = funccodi !== undefined ? `?funccodi=${funccodi}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/disc/acesso${qs}`, user)));
  },
  async discSolicitar(user: AtlasUser, funccodis: number[]): Promise<{ itens: DiscConvite[]; ignorados: DiscConviteIgnorado[] }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/disc/convites`, user, { funccodis }));
  },
  async discConvites(user: AtlasUser, params: { status?: DiscConviteStatus; funccodi?: number } = {}): Promise<{ itens: DiscConvite[]; resumo: Record<Exclude<DiscConviteStatus, 'substituido'>, number> }> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.funccodi !== undefined) qs.set('funccodi', String(params.funccodi));
    const sep = qs.toString() ? `?${qs.toString()}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/disc/convites${sep}`, user)));
  },
  async discCancelarConvite(user: AtlasUser, id: string): Promise<{ ok: true }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/disc/convites/${id}/cancelar`, user));
  },
  async discEnviarConviteEmail(user: AtlasUser, id: string, email?: string): Promise<{ ok: true; email: string }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/disc/convites/${id}/email`, user, email ? { email } : {}));
  },
  /** Página pública: sem usuário, o token é a credencial. */
  async discPublicoAbrir(token: string): Promise<DiscPublico> {
    return handle(await fetch(`${API_BASE}/api/atlas/disc/publico/${encodeURIComponent(token)}`));
  },
  async discPublicoEnviar(token: string, respostas: DiscResposta[]): Promise<{ ok: true }> {
    return handle(await fetch(`${API_BASE}/api/atlas/disc/publico/${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ respostas }),
    }));
  },

  // ===== DISC Turbinado (módulo #12) — Cargos + Fit + Mapa + Comparar + Sugestão =====
  async getCargos(user: AtlasUser, params: { departamento?: string; busca?: string } = {}): Promise<{ itens: Cargo[]; total: number }> {
    const qs = new URLSearchParams();
    if (params.departamento) qs.set('departamento', params.departamento);
    if (params.busca) qs.set('busca', params.busca);
    const sep = qs.toString() ? `?${qs.toString()}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/cargos${sep}`, user)));
  },
  async criarCargo(user: AtlasUser, body: Omit<Cargo, '_id' | 'empresaId'>): Promise<{ ok: true; cargo: Cargo }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/cargos`, user, body));
  },
  async getCargo(user: AtlasUser, id: string): Promise<Cargo> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/cargos/${id}`, user)));
  },
  async editarCargo(user: AtlasUser, id: string, body: Partial<Cargo>): Promise<{ ok: true; cargo: Cargo }> {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/cargos/${id}`, user, body));
  },
  async excluirCargo(user: AtlasUser, id: string): Promise<{ ok: true }> {
    return handle(await jsonReq('DELETE', `${API_BASE}/api/atlas/cargos/${id}`, user));
  },
  async getDiscMapaTime(user: AtlasUser, params: { cc?: string; departamento?: string } = {}): Promise<{ total: number; itens: DiscMapaItem[] }> {
    const qs = new URLSearchParams();
    if (params.cc) qs.set('cc', params.cc);
    if (params.departamento) qs.set('departamento', params.departamento);
    const sep = qs.toString() ? `?${qs.toString()}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/disc/mapa-time${sep}`, user)));
  },
  async getDiscFit(user: AtlasUser, funccodi: number): Promise<DiscFit> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/disc/fit/${funccodi}`, user)));
  },
  async getDiscComparar(user: AtlasUser, a: number, b: number): Promise<DiscComparar> {
    const qs = new URLSearchParams({ a: String(a), b: String(b) });
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/disc/comparar?${qs.toString()}`, user)));
  },
  async getDiscSugestaoCarreira(user: AtlasUser, funccodi: number): Promise<DiscSugestao> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/disc/sugestao-carreira/${funccodi}`, user)));
  },

  // Férias (passthrough GIX, read-only)
  async getFerias(user: AtlasUser, funccodi: number): Promise<FeriasResumo> {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/ferias/${funccodi}`, user));
    return handle(r);
  },

  async listarFeriasProgramadas(user: AtlasUser, limit?: number): Promise<{ itens: FeriasProgramada[]; total: number }> {
    const qs = limit ? `?limit=${limit}` : '';
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/ferias/programadas${qs}`, user));
    return handle(r);
  },

  // Holerite (sensível — acesso auditado)
  async getHolerite(user: AtlasUser, funccodi: number, mes?: string): Promise<Holerite> {
    const qs = mes ? `?mes=${encodeURIComponent(mes)}` : '';
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/holerite/${funccodi}${qs}`, user));
    return handle(r);
  },

  async listarCompetenciasHolerite(user: AtlasUser, funccodi: number): Promise<{ competencias: string[] }> {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/holerite/${funccodi}/competencias`, user));
    return handle(r);
  },

  // Auditoria (admin)
  async listarAuditoria(user: AtlasUser, params: {
    ator?: string; acao?: string; alvo_tipo?: string; alvo_id?: string;
    desde?: string; ate?: string; page?: number; per_page?: number;
  } = {}): Promise<{ itens: AuditLog[]; total: number; page: number; per_page: number }> {
    const qs = new URLSearchParams();
    if (params.ator) qs.set('ator', params.ator);
    if (params.acao) qs.set('acao', params.acao);
    if (params.alvo_tipo) qs.set('alvo_tipo', params.alvo_tipo);
    if (params.alvo_id) qs.set('alvo_id', params.alvo_id);
    if (params.desde) qs.set('desde', params.desde);
    if (params.ate) qs.set('ate', params.ate);
    if (params.page) qs.set('page', String(params.page));
    if (params.per_page) qs.set('per_page', String(params.per_page));
    const sep = qs.toString() ? `?${qs.toString()}` : '';
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/auditoria${sep}`, user));
    return handle(r);
  },

  // ===== Treinamentos =====
  async listarTreinamentos(user: AtlasUser, params: { status?: string; categoria?: string; obrigatorio?: boolean; page?: number; per_page?: number } = {}) {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.categoria) qs.set('categoria', params.categoria);
    if (params.obrigatorio) qs.set('obrigatorio', '1');
    if (params.page) qs.set('page', String(params.page));
    if (params.per_page) qs.set('per_page', String(params.per_page));
    const sep = qs.toString() ? `?${qs.toString()}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/treinamentos${sep}`, user)));
  },
  async criarTreinamento(user: AtlasUser, body: Record<string, any>) {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/treinamentos`, user, body));
  },
  async getTreinamento(user: AtlasUser, id: string) {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/treinamentos/${id}`, user)));
  },
  async editarTreinamento(user: AtlasUser, id: string, body: Record<string, any>) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/treinamentos/${id}`, user, body));
  },
  async inscreverTreinamento(user: AtlasUser, id: string, funccodi?: number) {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/treinamentos/${id}/inscrever`, user, funccodi !== undefined ? { funccodi } : {}));
  },
  async atualizarInscricao(user: AtlasUser, id: string, inscId: string, body: Record<string, any>) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/treinamentos/${id}/inscricoes/${inscId}`, user, body));
  },
  async treinamentosColaborador(user: AtlasUser, funccodi: number) {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/treinamentos/colaborador/${funccodi}`, user)));
  },

  // ===== Feedback 360 =====
  async listarCiclos(user: AtlasUser, params: { status?: string } = {}) {
    const qs = params.status ? `?status=${encodeURIComponent(params.status)}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/feedback/ciclos${qs}`, user)));
  },
  async criarCiclo(user: AtlasUser, body: Record<string, any>) {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/feedback/ciclos`, user, body));
  },
  async getCiclo(user: AtlasUser, id: string) {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/feedback/ciclos/${id}`, user)));
  },
  async editarCiclo(user: AtlasUser, id: string, body: Record<string, any>) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/feedback/ciclos/${id}`, user, body));
  },
  async publicarCiclo(user: AtlasUser, id: string) {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/feedback/ciclos/${id}/publicar`, user, {}));
  },
  async encerrarCiclo(user: AtlasUser, id: string) {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/feedback/ciclos/${id}/encerrar`, user, {}));
  },
  async minhasPendencias(user: AtlasUser): Promise<{ itens: FeedbackPendencia[]; total: number }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/feedback/minhas-pendencias`, user)));
  },
  async responderFeedback(user: AtlasUser, body: { ciclo_id: string; avaliado_funccodi: number; tipo_relacao: string; respostas: any[] }) {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/feedback/responder`, user, body));
  },
  async relatorioFeedback(user: AtlasUser, cicloId: string, funccodi: number): Promise<FeedbackRelatorio> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/feedback/relatorio/${cicloId}/${funccodi}`, user)));
  },

  // ===== Vagas =====
  async listarVagas(user: AtlasUser, params: { status?: string } = {}): Promise<{ itens: Vaga[]; total: number }> {
    const qs = params.status ? `?status=${encodeURIComponent(params.status)}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/vagas${qs}`, user)));
  },
  async criarVaga(user: AtlasUser, body: Record<string, any>) {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/vagas`, user, body));
  },
  async getVaga(user: AtlasUser, id: string): Promise<{ vaga: Vaga; candidatos: VagaCandidato[] }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/vagas/${id}`, user)));
  },
  async editarVaga(user: AtlasUser, id: string, body: Record<string, any>) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/vagas/${id}`, user, body));
  },
  async encerrarVaga(user: AtlasUser, id: string) {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/vagas/${id}/encerrar`, user, {}));
  },
  async preencherVaga(user: AtlasUser, id: string, candidatoId: string) {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/vagas/${id}/preencher`, user, { candidato_id: candidatoId }));
  },
  async criarCandidato(user: AtlasUser, vagaId: string, body: Record<string, any>) {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/vagas/${vagaId}/candidatos`, user, body));
  },
  async atualizarCandidato(user: AtlasUser, vagaId: string, candId: string, body: Record<string, any>) {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/vagas/${vagaId}/candidatos/${candId}`, user, body));
  },
  async discCandidato(user: AtlasUser, vagaId: string, candId: string): Promise<{ item: DiscConvite }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/vagas/${vagaId}/candidatos/${candId}/disc`, user));
  },
  /** Relatório DISC completo do candidato (mesmo formato de discColaborador). */
  async discCandidatoRelatorio(user: AtlasUser, vagaId: string, candId: string): Promise<DiscColaborador & { nome?: string }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/vagas/${vagaId}/candidatos/${candId}/disc`, user)));
  },
  async discCandidatoPdf(user: AtlasUser, vagaId: string, candId: string): Promise<{ blob: Blob; nomeArquivo: string }> {
    const r = await fetch(withUserParam(`${API_BASE}/api/atlas/vagas/${vagaId}/candidatos/${candId}/disc/pdf`, user));
    if (!r.ok) return handle(r);
    const cd = r.headers.get('Content-Disposition') || '';
    const m = /filename="([^"]+)"/.exec(cd);
    return { blob: await r.blob(), nomeArquivo: m ? m[1] : 'perfil-disc.pdf' };
  },
  async minhasIndicacoes(user: AtlasUser) {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/vagas/minhas-indicacoes`, user)));
  },

  // ===== Onboarding (módulo #13) =====
  async getOnboardingTrilhas(user: AtlasUser): Promise<{ itens: OnboardingTrilha[] }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/onboarding/trilhas`, user)));
  },
  async criarOnboardingTrilha(user: AtlasUser, body: Omit<OnboardingTrilha, '_id' | 'empresaId' | 'criado_em'>): Promise<OnboardingTrilha> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/onboarding/trilhas`, user, body));
  },
  async atualizarOnboardingTrilha(user: AtlasUser, id: string, body: Partial<OnboardingTrilha>): Promise<{ ok: true }> {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/onboarding/trilhas/${id}`, user, body));
  },
  async excluirOnboardingTrilha(user: AtlasUser, id: string): Promise<{ ok: true }> {
    return handle(await jsonReq('DELETE', `${API_BASE}/api/atlas/onboarding/trilhas/${id}`, user));
  },
  async getOnboardingExecucoes(user: AtlasUser, params: { status?: string; funccodi?: number } = {}): Promise<{ itens: OnboardingExecucao[] }> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.funccodi !== undefined) qs.set('funccodi', String(params.funccodi));
    const sep = qs.toString() ? `?${qs.toString()}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/onboarding/execucoes${sep}`, user)));
  },
  async criarOnboardingExecucao(user: AtlasUser, funccodi: number, trilhaId: string): Promise<OnboardingExecucao> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/onboarding/execucoes`, user, { funccodi, trilha_id: trilhaId }));
  },
  async marcarEtapaOnboarding(user: AtlasUser, execId: string, etapaId: string, status: 'concluido' | 'pendente', observacao?: string): Promise<{ ok: true; progresso_pct: number; status: string }> {
    const body: Record<string, any> = { status };
    if (observacao !== undefined) body.observacao = observacao;
    return handle(await jsonReq('PATCH', `${API_BASE}/api/atlas/onboarding/execucoes/${execId}/etapa/${etapaId}`, user, body));
  },
  async cancelarOnboardingExecucao(user: AtlasUser, execId: string): Promise<{ ok: true }> {
    return handle(await jsonReq('PATCH', `${API_BASE}/api/atlas/onboarding/execucoes/${execId}`, user, { status: 'cancelado' }));
  },

  // ===== Avaliação de Desempenho (módulo #11) =====
  async getFormularios(user: AtlasUser): Promise<{ itens: AvaliacaoFormulario[] }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/avaliacao/formularios`, user)));
  },
  async criarFormulario(user: AtlasUser, body: Omit<AvaliacaoFormulario, '_id' | 'empresaId'>): Promise<AvaliacaoFormulario> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/avaliacao/formularios`, user, body));
  },
  async getFormulario(user: AtlasUser, fid: string): Promise<AvaliacaoFormulario> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/avaliacao/formularios/${fid}`, user)));
  },
  async editarFormulario(user: AtlasUser, fid: string, body: Partial<AvaliacaoFormulario>): Promise<{ ok: true }> {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/avaliacao/formularios/${fid}`, user, body));
  },
  async excluirFormulario(user: AtlasUser, fid: string): Promise<{ ok: true }> {
    return handle(await jsonReq('DELETE', `${API_BASE}/api/atlas/avaliacao/formularios/${fid}`, user));
  },
  async getCiclosAval(user: AtlasUser, params: { status?: string } = {}): Promise<{ itens: AvaliacaoCiclo[] }> {
    const qs = params.status ? `?status=${encodeURIComponent(params.status)}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/avaliacao/ciclos${qs}`, user)));
  },
  async criarCicloAval(user: AtlasUser, body: Record<string, any>): Promise<AvaliacaoCiclo> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/avaliacao/ciclos`, user, body));
  },
  async getCicloAval(user: AtlasUser, cid: string): Promise<AvaliacaoCiclo> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/avaliacao/ciclos/${cid}`, user)));
  },
  async editarCicloAval(user: AtlasUser, cid: string, body: Record<string, any>): Promise<{ ok: true }> {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/avaliacao/ciclos/${cid}`, user, body));
  },
  async publicarCicloAval(user: AtlasUser, cid: string): Promise<{ ok: true }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/avaliacao/ciclos/${cid}/publicar`, user, {}));
  },
  async encerrarCicloAval(user: AtlasUser, cid: string): Promise<{ ok: true }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/avaliacao/ciclos/${cid}/encerrar`, user, {}));
  },
  async getMinhasPendentes(user: AtlasUser): Promise<{ itens: AvaliacaoPendente[]; total: number }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/avaliacao/minhas-pendentes`, user)));
  },
  async getFormularioResponder(user: AtlasUser, cid: string, funccodi: number): Promise<FormularioParaResponder> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/avaliacao/formulario-responder/${cid}/${funccodi}`, user)));
  },
  async enviarResposta(user: AtlasUser, body: { ciclo_id: string; avaliado_funccodi: number; tipo_avaliacao: string; respostas: RespostaItem[] }): Promise<{ ok: true }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/avaliacao/respostas`, user, body));
  },
  async getResultadoAval(user: AtlasUser, cid: string, funccodi: number): Promise<AvaliacaoResultado> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/avaliacao/resultado/${cid}/${funccodi}`, user)));
  },

  // ===== PDI (Plano de Desenvolvimento Individual — módulo #11) =====
  async getPdi(user: AtlasUser, funccodi: number): Promise<Pdi> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/pdi/${funccodi}`, user)));
  },
  async salvarPdi(user: AtlasUser, funccodi: number, body: { ciclo_origem_id?: string | null; objetivos: PdiObjetivo[] }): Promise<{ ok: true }> {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/pdi/${funccodi}`, user, body));
  },
  async concluirAcaoPdi(user: AtlasUser, funccodi: number, acaoId: string, concluida?: boolean): Promise<{ ok: true }> {
    return handle(await jsonReq('PATCH', `${API_BASE}/api/atlas/pdi/${funccodi}/acoes/${acaoId}/concluir`, user, concluida !== undefined ? { concluida } : {}));
  },

  // ===== Pesquisa de Clima / eNPS (módulo #15) =====
  async listarPesquisas(user: AtlasUser, params: { status?: string; tipo?: string } = {}): Promise<{ itens: Pesquisa[]; total: number }> {
    const qs = new URLSearchParams();
    if (params.status) qs.set('status', params.status);
    if (params.tipo) qs.set('tipo', params.tipo);
    const sep = qs.toString() ? `?${qs.toString()}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/pesquisas${sep}`, user)));
  },
  async criarPesquisa(user: AtlasUser, body: NovaPesquisa): Promise<{ ok: true; pesquisa: Pesquisa }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/pesquisas`, user, body));
  },
  async getPesquisa(user: AtlasUser, id: string): Promise<{ pesquisa: Pesquisa }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/pesquisas/${id}`, user)));
  },
  async editarPesquisa(user: AtlasUser, id: string, body: Partial<NovaPesquisa>): Promise<{ ok: true; pesquisa: Pesquisa }> {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/pesquisas/${id}`, user, body));
  },
  async excluirPesquisa(user: AtlasUser, id: string): Promise<{ ok: true }> {
    return handle(await jsonReq('DELETE', `${API_BASE}/api/atlas/pesquisas/${id}`, user));
  },
  async publicarPesquisa(user: AtlasUser, id: string): Promise<{ ok: true; status: string }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/pesquisas/${id}/publicar`, user, {}));
  },
  async encerrarPesquisa(user: AtlasUser, id: string): Promise<{ ok: true; status: string }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/pesquisas/${id}/encerrar`, user, {}));
  },
  async getPesquisasPendentes(user: AtlasUser): Promise<{ itens: { _id: string; nome: string; tipo: PesquisaTipo }[]; total: number }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/pesquisas/pendentes`, user)));
  },
  async getMinhaPesquisa(user: AtlasUser, id: string): Promise<{ pendente: boolean; motivo?: string; pesquisa?: Pesquisa }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/pesquisas/${id}/minha`, user)));
  },
  async responderPesquisa(user: AtlasUser, id: string, respostas: RespostaPesquisaItem[]): Promise<{ ok: true }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/pesquisas/${id}/responder`, user, { respostas }));
  },
  async getResultadoPesquisa(user: AtlasUser, id: string): Promise<ResultadoPesquisa> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/pesquisas/${id}/resultado`, user)));
  },

  // ---- Kudos / Reconhecimento (#17) ----
  async getKudosFeed(user: AtlasUser, limit = 50): Promise<{ itens: Kudos[]; total: number }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/kudos/feed?limit=${limit}`, user)));
  },
  async getKudosSemana(user: AtlasUser): Promise<{ itens: Kudos[]; total: number }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/kudos/semana`, user)));
  },
  async criarKudos(user: AtlasUser, body: NovoKudos): Promise<{ ok: true; kudos: Kudos }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/kudos`, user, body));
  },
  async getKudosPerfil(user: AtlasUser, funccodi: number): Promise<{ itens: Kudos[]; total: number; por_valor: Record<string, number> }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/kudos/perfil/${funccodi}`, user)));
  },
  async curtirKudos(user: AtlasUser, id: string): Promise<{ ok: true; curti: boolean; curtidas_count: number }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/kudos/${id}/curtir`, user, {}));
  },

  // ---- Aniversariantes / Lembretes (#19) ----
  async getAniversariantes(user: AtlasUser, dia: 'hoje' | 'mes' = 'hoje'): Promise<{ itens: Aniversariante[]; total: number; escopo: string }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/lembretes/aniversariantes?dia=${dia}`, user)));
  },
  async getAniversariosEmpresa(user: AtlasUser): Promise<{ itens: Aniversariante[]; total: number }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/lembretes/aniversarios-empresa`, user)));
  },
  async getExperiencias(user: AtlasUser, tolerancia = 5): Promise<{ itens: MarcoExperiencia[]; total: number }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/lembretes/experiencias?tolerancia=${tolerancia}`, user)));
  },

  // ---- Documentos do Colaborador (#16) ----
  async getDocumentosColaborador(user: AtlasUser, funccodi: number): Promise<{ itens: DocumentoColab[]; total: number; funccodi: number }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/colaboradores/${funccodi}/documentos`, user)));
  },
  async criarDocumento(user: AtlasUser, funccodi: number, body: NovoDocumento): Promise<{ ok: true; documento: DocumentoColab }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/colaboradores/${funccodi}/documentos`, user, body));
  },
  async editarDocumento(user: AtlasUser, id: string, body: Partial<NovoDocumento>): Promise<{ ok: true; documento: DocumentoColab }> {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/documentos/${id}`, user, body));
  },
  async excluirDocumento(user: AtlasUser, id: string): Promise<{ ok: true }> {
    return handle(await jsonReq('DELETE', `${API_BASE}/api/atlas/documentos/${id}`, user));
  },
  async getDocumentosVencimentos(user: AtlasUser, dias = 30): Promise<{ itens: DocumentoColab[]; total: number; dias: number }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/documentos/vencimentos?dias=${dias}`, user)));
  },

  // ---- Sucessão & 9-box (#18) ----
  async getNoveBox(user: AtlasUser, departamento?: string): Promise<NoveBoxResultado> {
    const q = departamento ? `?departamento=${encodeURIComponent(departamento)}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/sucessao/9box${q}`, user)));
  },
  async getSucessores(user: AtlasUser, funccodi: number): Promise<{ alvo: PessoaNoveBox; candidatos: Sucessor[]; total: number }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/sucessao/sucessores/${funccodi}`, user)));
  },

  // ---- Offboarding / Desligamento (#20) ----
  async getOffboardings(user: AtlasUser, status?: OffboardingStatus): Promise<{ itens: Offboarding[]; total: number }> {
    const q = status ? `?status=${status}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/offboarding${q}`, user)));
  },
  async criarOffboarding(user: AtlasUser, body: NovoOffboarding): Promise<{ ok: true; offboarding: Offboarding }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/offboarding`, user, body));
  },
  async getOffboarding(user: AtlasUser, id: string): Promise<{ offboarding: Offboarding }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/offboarding/${id}`, user)));
  },
  async atualizarOffboarding(user: AtlasUser, id: string, body: Partial<Offboarding>): Promise<{ ok: true; offboarding: Offboarding }> {
    return handle(await jsonReq('PATCH', `${API_BASE}/api/atlas/offboarding/${id}`, user, body));
  },
  async excluirOffboarding(user: AtlasUser, id: string): Promise<{ ok: true }> {
    return handle(await jsonReq('DELETE', `${API_BASE}/api/atlas/offboarding/${id}`, user));
  },
  async getOffboardingRelatorio(user: AtlasUser): Promise<OffboardingRelatorio> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/offboarding/relatorio`, user)));
  },

  // ---- Ocorrências disciplinares (advertências / suspensões) ----
  async getOcorrencias(user: AtlasUser, params: {
    funccodi?: number; tipo?: OcorrenciaTipo | ''; status?: OcorrenciaStatus | '';
    desde?: string; ate?: string;
  } = {}): Promise<{ itens: Ocorrencia[]; total: number; resumo: Record<string, number> }> {
    const qs = new URLSearchParams();
    if (params.funccodi !== undefined) qs.set('funccodi', String(params.funccodi));
    if (params.tipo) qs.set('tipo', params.tipo);
    if (params.status) qs.set('status', params.status);
    if (params.desde) qs.set('desde', params.desde);
    if (params.ate) qs.set('ate', params.ate);
    const sep = qs.toString() ? `?${qs.toString()}` : '';
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/disciplinar${sep}`, user)));
  },
  async getOcorrenciasColaborador(user: AtlasUser, funccodi: number): Promise<{ itens: Ocorrencia[]; total: number }> {
    return handle(await fetch(withUserParam(`${API_BASE}/api/atlas/disciplinar/colaborador/${funccodi}`, user)));
  },
  async criarOcorrencia(user: AtlasUser, body: NovaOcorrencia): Promise<{ ok: true; ocorrencia: Ocorrencia }> {
    return handle(await jsonReq('POST', `${API_BASE}/api/atlas/disciplinar`, user, body));
  },
  async editarOcorrencia(user: AtlasUser, id: string, body: Partial<NovaOcorrencia>): Promise<{ ok: true; ocorrencia: Ocorrencia }> {
    return handle(await jsonReq('PUT', `${API_BASE}/api/atlas/disciplinar/${id}`, user, body));
  },
  async excluirOcorrencia(user: AtlasUser, id: string): Promise<{ ok: true }> {
    return handle(await jsonReq('DELETE', `${API_BASE}/api/atlas/disciplinar/${id}`, user));
  },
};

// ===== Filiais =====
export interface Filial {
  funcempe: number | null;
  nome: string;
  total: number;
}

// ===== Ocorrências disciplinares =====
export type OcorrenciaTipo =
  | 'orientacao' | 'advertencia_verbal' | 'advertencia_escrita' | 'suspensao' | 'elogio';
export type OcorrenciaGravidade = 'leve' | 'media' | 'grave';
export type OcorrenciaStatus = 'registrada' | 'ciente' | 'contestada' | 'anulada';

export interface Ocorrencia {
  _id: string;
  empresaId?: string;
  funccodi: number;
  colaborador_nome?: string | null;
  colaborador_cargo?: string | null;
  colaborador_cc?: string | null;
  funcempe?: number | null;
  tipo: OcorrenciaTipo;
  data: string;
  motivo: string;
  descricao?: string | null;
  gravidade: OcorrenciaGravidade;
  dias_suspensao?: number | null;
  data_inicio_suspensao?: string | null;
  aplicada_por?: string | null;
  testemunhas?: string[];
  status: OcorrenciaStatus;
  ciente_em?: string | null;
  documento_url?: string | null;
  criado_em?: string;
  criado_por?: string;
}

export interface NovaOcorrencia {
  funccodi: number;
  tipo: OcorrenciaTipo;
  data: string;
  motivo: string;
  descricao?: string;
  gravidade?: OcorrenciaGravidade;
  dias_suspensao?: number | null;
  data_inicio_suspensao?: string | null;
  aplicada_por?: string;
  testemunhas?: string[];
  status?: OcorrenciaStatus;
  ciente_em?: string | null;
  documento_url?: string;
}

export const LABEL_OCORRENCIA: Record<OcorrenciaTipo, string> = {
  orientacao: 'Orientação',
  advertencia_verbal: 'Advertência verbal',
  advertencia_escrita: 'Advertência escrita',
  suspensao: 'Suspensão',
  elogio: 'Elogio',
};

// Tipos
export type AtlasStatus = 'ativo' | 'demitido' | 'removido_gix';

// ===== Dashboard RH / People Analytics (módulo #10) =====
export interface DistribFaixa { faixa: string; total: number; }

export interface DashboardHeadcountCC { codigo: string; nome: string; total: number; }
export interface DashboardHeadcountDepto { codigo: string; total: number; }

export interface DashboardHeadcount {
  total: number;
  ativos: number;
  demitidos: number;
  por_centro_custo: DashboardHeadcountCC[];
  por_departamento: DashboardHeadcountDepto[];
}

export interface DashboardTempoCasa {
  media_anos: number;
  mediana_anos: number;
  distribuicao: DistribFaixa[];
}

export interface DashboardIdade {
  media: number;
  distribuicao: DistribFaixa[];
}

export interface DashboardNivelTotal { nivel: string; total: number; }
export interface DashboardTipoTotal { tipo: string; total: number; }

export interface DashboardDisc {
  respondido_pct: number;
  distribuicao: Record<DiscFator, number>;
}

export interface DashboardAniversariante {
  funccodi: number;
  nome: string;
  data_nascimento: string;
  dia: number;
  mes: number;
  dias_ate: number;
}

export interface DashboardVagas {
  abertas: number;
  por_status: Record<string, number>;
}

export interface DashboardCustoQuadroCC { codigo: string; nome: string; total: number; }
export interface DashboardCustoQuadro {
  total: number;
  por_centro_custo: DashboardCustoQuadroCC[];
}

export interface DashboardResumo {
  headcount: DashboardHeadcount;
  tempo_casa: DashboardTempoCasa;
  idade: DashboardIdade;
  escolaridade: DashboardNivelTotal[];
  estado_civil: DashboardTipoTotal[];
  fichas_completas_pct: number;
  disc: DashboardDisc;
  aniversariantes_30d: DashboardAniversariante[];
  vagas: DashboardVagas;
  custo_quadro: DashboardCustoQuadro | null;
}

export interface TurnoverMes {
  ym: string;
  label: string;
  admitidos: number;
  demitidos: number;
  headcount_fim: number;
  turnover_pct: number;
}

export interface HeadcountMes {
  ym: string;
  label: string;
  headcount: number;
}

export interface DadosPublicos {
  funccodi: number;
  funcempe?: number | null;   // filial do GIX
  nome?: string;
  matricula?: number;
  data_admissao?: string;
  data_demissao?: string | null;
  cargo_codigo?: number;
  cargo_nome?: string;
  centro_custo_codigo?: number;
  centro_custo_nome?: string;
  departamento_codigo?: string;
  email_corporativo?: string;
  nome_mae?: string;
  nome_pai?: string;
  data_nascimento?: string;
  raca?: string;
  estado_civil?: number;
  nacionalidade?: number;
  escolaridade?: number;
  rg?: string;
  endereco?: string;
  status: AtlasStatus;
  tem_foto_gix?: boolean;
}

// ===== Cadastros enriquecidos (módulo #22) =====
export interface Dependente {
  nome?: string;
  parentesco?: string;
  data_nascimento?: string;
  cpf?: string;
  ir?: boolean;
  plano_saude?: boolean;
}

export interface Familia {
  estado_civil?: string | null;
  conjuge_nome?: string | null;
  conjuge_data_nascimento?: string | null;
  conjuge_telefone?: string | null;
  qtd_filhos?: number | null;
  observacoes?: string | null;
}

export type PecaUniforme = 'camisa' | 'calca' | 'calcado' | 'jaleco' | 'epi';

export interface EntregaUniforme {
  item?: string;
  tamanho?: string | null;
  quantidade?: number;
  data_entrega?: string | null;
  devolvido?: boolean;
  observacao?: string | null;
}

export interface Uniforme {
  tamanhos?: Partial<Record<PecaUniforme, string | null>>;
  entregas?: EntregaUniforme[];
  observacoes?: string | null;
}

export type Genero = 'masculino' | 'feminino' | 'nao_binario' | 'outro' | 'nao_informado';

export const LABEL_GENERO: Record<Genero, string> = {
  masculino: 'Masculino',
  feminino: 'Feminino',
  nao_binario: 'Não binário',
  outro: 'Outro',
  nao_informado: 'Prefiro não informar',
};

export const LABEL_PECA_UNIFORME: Record<PecaUniforme, string> = {
  camisa: 'Camisa',
  calca: 'Calça',
  calcado: 'Calçado',
  jaleco: 'Jaleco',
  epi: 'EPI',
};

export interface ContatoEmergencia {
  nome?: string;
  parentesco?: string;
  telefone?: string;
}

export interface Contato {
  celular?: string;
  telefone_fixo?: string;
  email_pessoal?: string;
  emergencia?: ContatoEmergencia;
}

export interface Endereco {
  cep?: string;
  logradouro?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  uf?: string;
}

export interface Formacao {
  nivel?: string;        // medio | tecnico | superior | pos | mestrado | doutorado
  curso?: string;
  instituicao?: string;
  ano_conclusao?: string;
  situacao?: string;     // concluido | em_andamento | trancado
}

export interface Idioma {
  idioma?: string;
  nivel?: string;        // basico | intermediario | avancado | fluente | nativo
}

export interface Certificacao {
  nome?: string;
  emissor?: string;
  data_emissao?: string;
  data_validade?: string;
}

export interface Ctps {
  numero?: string;
  serie?: string;
  uf?: string;
}

export interface Cnh {
  numero?: string;
  categoria?: string;
  validade?: string;
}

export interface DocumentosPessoais {
  cpf?: string;
  rg?: string;
  pis?: string;
  titulo_eleitor?: string;
  ctps?: Ctps;
  cnh?: Cnh;
  reservista?: string;
}

export interface HistoricoCargo {
  data_inicio?: string;
  data_fim?: string;
  cargo?: string;
  centro_custo?: string;
  salario?: string | number;
  motivo?: string;
}

export interface Enriquecimento {
  foto_url?: string;
  telefone_pessoal?: string;
  celular?: string;
  email_pessoal?: string;
  endereco_complemento?: string;
  observacoes?: string;
  contato_emergencia?: string;
  idiomas?: Idioma[] | string;   // string = dado antigo; array = formato novo
  competencias?: string;
  hobbies?: string;
  tamanho_camisa?: string;
  gestor_funccodi?: number | null;
  genero?: Genero | null;
  nome_social?: string;
  // Cadastros enriquecidos (módulo #22)
  dependentes?: Dependente[];
  familia?: Familia;
  uniforme?: Uniforme;
  contato?: Contato;
  endereco?: Endereco;
  formacao?: Formacao[];
  certificacoes?: Certificacao[];
  tags?: string[];
}

export interface Colaborador {
  _id: string;
  funccodi: number;
  empresaId: string;
  origem: 'gix' | 'mongo' | 'ambos';
  dados_publicos: DadosPublicos;
  enriquecimento?: Enriquecimento;
  cpf?: string;       // só preenchido se permissão rh_total
  salario?: string;   // só preenchido se permissão rh_total
  rg?: string;        // só preenchido se permissão rh_total
  documentos_pessoais?: DocumentosPessoais;  // só rh_total (descriptografado)
  historico_cargos?: HistoricoCargo[];       // só rh_total (descriptografado)
  _sensiveis_ocultos?: boolean;
  data_sincronizacao?: string;
  criado_em?: string;
}

export interface NovoColaboradorManual {
  nome: string;
  matricula?: string;
  cargo_nome?: string;
  centro_custo_nome?: string;
  departamento_codigo?: string;
  email_corporativo?: string;
  data_admissao?: string;
  data_nascimento?: string;
  gestor_funccodi?: number | null;
  status?: AtlasStatus;
  empresaId?: string;
}

export interface HierarquiaNodo {
  funccodi: number;
  nome?: string;
  cargo_nome?: string;
  centro_custo_nome?: string;
  tem_foto?: boolean;
  gestor_funccodi?: number | null;
  subordinados: HierarquiaNodo[];
}

export interface HierarquiaResp {
  raizes: HierarquiaNodo[];
  sem_hierarquia: HierarquiaNodo[];
  total: number;
}

export interface HierarquiaSugestao {
  funccodi: number;
  nome?: string;
  cargo_nome?: string;
  centro_custo_nome?: string;
  nivel: number;
  gestor_sugerido: number;
  gestor_nome?: string;
  gestor_cargo?: string;
  gestor_nivel: number;
}

export interface HierarquiaAutoSugerirResp {
  sugestoes: HierarquiaSugestao[];
  total_sugeridos: number;
  total_colaboradores: number;
  sem_cargo_reconhecido: number;
  aplicados: number;
  aplicado: boolean;
}

export type NivelAtlas = 'visualizar' | 'editar' | 'rh_total';

export interface PermissaoUsuario {
  _id: string;
  email: string;
  nome?: string;
  role?: string;
  is_admin?: boolean;
  empresaId?: string;
  nivel_atlas: NivelAtlas | null;
}

// ===== DISC =====
export type DiscFator = 'D' | 'I' | 'S' | 'C';

export interface DiscGrupo {
  id: number;
  adjetivos: Record<DiscFator, string>;
}

export interface DiscResposta {
  grupo_id: number;
  mais: DiscFator;
  menos: DiscFator;
}

export interface DiscPerfil {
  natural: Record<DiscFator, number>;
  adaptado: Record<DiscFator, number>;
  /** Ausente em laudo importado do Sólides, que não publica equivalente. */
  combinado?: Record<DiscFator, number>;
  estilo_principal: DiscFator;
  estilo_secundario: DiscFator;
  descricao_principal: string;
}

export interface DiscFatorPN {
  fator: DiscFator;
  label: string;
  positivo: number;
  negativo: number;
}
export interface DiscNivelItem {
  nome: string;
  valor: number;
  nivel: string;
}
export interface DiscPerfilIsolado {
  nome: string;
  fator: DiscFator;
  valor: number;
}
export interface DiscTalento {
  nome: string;
  valor: number;
  nivel: string;
  x: number;
  y: number;
}
export interface DiscAnalise {
  perfil_predominante: string;
  subcaracteristicas: string[];
  habilidades_basicas: string;
  habilidades_comuns: string;
  relacionamento: string;
  tomada_decisao: string;
  estilo_lideranca: string;
  fatores: DiscFatorPN[];
  perfil_isolado: DiscPerfilIsolado[];
  indicadores: DiscNivelItem[];
  competencias: DiscNivelItem[];
  talentos: DiscTalento[];
  posicao: { x: number; y: number };
}

/** 'solides' = laudo do Profiler importado; 'atlas' = questionário respondido aqui. */
export type DiscOrigem = 'atlas' | 'solides';

export interface DiscColaborador {
  perfil: {
    respondido_em: string; perfil: DiscPerfil; analise?: DiscAnalise; origem?: DiscOrigem;
  } | null;
  historico: Array<{ respondido_em: string; estilo_principal: DiscFator; origem?: DiscOrigem }>;
}

export interface DiscTimePessoa {
  funccodi: number;
  nome?: string;
  cargo?: string;
  estilo_principal: DiscFator | null;
  foto_url?: string;
}

export interface DiscTime {
  total_respondidos: number;
  total_colaboradores: number;
  distribuicao: Record<DiscFator, number>;
  por_pessoa: DiscTimePessoa[];
}

export interface DiscAcesso {
  rh: boolean;
  /** Quantos liderados (cadeia toda) o gestor tem; null para o RH. */
  liderados: number | null;
  pode_ver?: boolean;
}

export type DiscConviteStatus = 'pendente' | 'expirado' | 'respondido' | 'cancelado' | 'substituido';

export interface DiscConvite {
  id: string;
  funccodi: number | null;
  candidato_id?: string | null;
  vaga_id?: string | null;
  nome?: string;
  cargo?: string;
  email_sugerido?: string | null;
  status: DiscConviteStatus;
  criado_em: string;
  criado_por?: string;
  expira_em: string;
  acessado_em?: string | null;
  respondido_em?: string | null;
  email_enviado_em?: string | null;
  email_enviado_para?: string | null;
  /** Só vem enquanto o link está valendo. */
  token?: string;
  link?: string;
}

export interface DiscConviteIgnorado { funccodi: number; nome?: string; motivo: string }

export interface DiscPublico {
  nome: string;
  empresa?: string | null;
  expira_em: string;
  grupos: DiscGrupo[];
}

/** Link que o RH copia: mesmo host em que ele está logado. */
export function linkDiscPublico(token: string): string {
  return `${window.location.origin}/disc/${token}`;
}

// ===== DISC Turbinado (módulo #12) =====
export interface PerfilDisc { D: number; I: number; S: number; C: number }

export interface FaixaSalarial {
  min?: number | null;
  medio?: number | null;
  max?: number | null;
}

export interface Cargo {
  _id: string;
  empresaId: string;
  codigo: string;
  nome: string;
  departamento?: string;
  perfil_disc_ideal: PerfilDisc;
  faixa_salarial?: FaixaSalarial;
  nivel: number;
  descricao?: string;
  responsabilidades: string[];
  requisitos: string[];
  ativo?: boolean;
}

export interface DiscMapaItem {
  funccodi: number;
  nome: string;
  cargo?: string;
  estilo_principal: string;
  perfil: PerfilDisc;
}

export type DiscFitStatus = 'ok' | 'sem_disc' | 'sem_cargo' | 'cargo_sem_perfil';

export interface DiscFit {
  funccodi: number;
  nome?: string;
  cargo: { codigo: string; nome: string } | null;
  perfil_real: PerfilDisc | null;
  perfil_ideal: PerfilDisc | null;
  gaps: PerfilDisc | null;
  fit_pct: number | null;
  status: DiscFitStatus;
}

// ===== Pesquisa de Clima / eNPS (módulo #15) =====
export type PesquisaTipo = 'enps' | 'clima' | 'pulse';
export type PesquisaStatus = 'rascunho' | 'ativa' | 'encerrada';
export type PerguntaTipo = 'nps' | 'escala_5' | 'texto' | 'sim_nao';

export interface PerguntaPesquisa {
  id: number | string;
  texto: string;
  tipo: PerguntaTipo;
}

export interface Pesquisa {
  _id: string;
  empresaId?: string;
  tipo: PesquisaTipo;
  nome: string;
  descricao?: string;
  perguntas?: PerguntaPesquisa[];
  inicio?: string | null;
  fim?: string | null;
  anonima: boolean;
  escopo?: { departamentos: string[] };
  status: PesquisaStatus;
  criado_em?: string;
  criado_por_email?: string;
}

export interface NovaPesquisa {
  tipo: PesquisaTipo;
  nome: string;
  descricao?: string;
  perguntas: PerguntaPesquisa[];
  inicio?: string | null;
  fim?: string | null;
  anonima?: boolean;
  escopo?: { departamentos: string[] };
}

export interface RespostaPesquisaItem {
  pergunta_id: number | string;
  valor: number | string;
}

export interface NpsResultado {
  nps: number | null;
  promotores: number;
  neutros: number;
  detratores: number;
  total: number;
}

export interface ResultadoPergunta {
  pergunta_id: number | string;
  texto: string;
  tipo: PerguntaTipo;
  total: number;
  nps?: NpsResultado;
  media?: number | null;
  distribuicao?: Record<string, number>;
  por_departamento?: Record<string, NpsResultado | number | null>;
  sim?: number;
  nao?: number;
  textos?: string[];
}

export interface ResultadoPesquisa {
  liberado: boolean;
  minimo?: number;
  total_respostas: number;
  enps?: number | null;
  pesquisa: { nome: string; tipo: PesquisaTipo; anonima?: boolean; status?: PesquisaStatus };
  perguntas?: ResultadoPergunta[];
}

export interface DiscPessoaCmp {
  funccodi: number;
  nome?: string;
  cargo?: string;
  estilo_principal: string | null;
  perfil: PerfilDisc | null;
}

export interface DiscComparar {
  a: DiscPessoaCmp;
  b: DiscPessoaCmp;
  gaps: PerfilDisc;
  dicas: string[];
}

export interface DiscSugestaoItem {
  codigo: string;
  nome: string;
  departamento?: string;
  fit_pct: number;
  perfil_ideal: PerfilDisc;
}

export interface DiscSugestao {
  funccodi: number;
  perfil_real: PerfilDisc | null;
  sugestoes: DiscSugestaoItem[];
}

// ===== Férias =====
export interface FeriasPeriodo {
  inicio: string;
  fim: string;
  dias_direito: number;
  dias_gozados: number;
  dias_disponiveis: number;
  vence_em: string;
}

export interface FeriasHistoricoItem {
  inicio_programado: string | null;
  inicio_concedido: string | null;
  dias: number | null;
  tipo: string | null;       // código bruto do GIX (ex.: "A")
  descricao?: string | null; // descrição do evento (arqevef), quando disponível
  valor: number | null;
  periodo_inicio: string | null;
  periodo_fim: string | null;
}

export interface FeriasResumo {
  funccodi: number;
  saldo_dias: number | null;
  periodo_aquisitivo_atual: FeriasPeriodo | null;
  historico: FeriasHistoricoItem[];
}

export interface FeriasProgramada {
  funccodi: number;
  nome?: string;
  cargo_nome?: string;
  centro_custo_codigo?: number | string;
  centro_custo_nome?: string;
  inicio_programado: string | null;
  dias: number | null;
}

// ===== Holerite =====
export interface HoleriteLanc {
  evento: number | null;
  descricao: string;
  tipo: 'V' | 'D' | 'O' | null;  // V=vencimento (provento), D=desconto, O=outros
  referencia: number | null;
  valor: number;
}

export interface Holerite {
  funccodi: number;
  competencia: string | null;
  lancamentos: HoleriteLanc[];
  totais: { vencimentos: number; descontos: number; liquido: number };
}

// ===== Auditoria =====
export interface AuditLog {
  _id: string;
  timestamp: string;
  ator_email: string;
  acao: string;
  alvo_tipo: string;
  alvo_id: string;
  antes?: any;
  depois?: any;
  contexto?: any;
}

// ===== Treinamentos =====
export type TreinamentoStatus = 'rascunho' | 'publicado' | 'encerrado';
export type InscricaoStatus = 'inscrito' | 'presente' | 'ausente' | 'concluido' | 'cancelado';

export interface Treinamento {
  _id: string;
  empresaId?: string;
  titulo: string;
  descricao?: string;
  categoria?: string;
  modalidade?: 'online' | 'presencial' | 'hibrido';
  carga_horaria?: number;
  instrutor?: string;
  local?: string;
  data_inicio?: string;
  data_fim?: string;
  vagas?: number;
  vagas_preenchidas?: number;
  obrigatorio?: boolean;
  departamentos_alvo?: (number | string)[];
  status: TreinamentoStatus;
  criado_em?: string;
  criado_por_email?: string;
  ativo?: boolean;
}

export interface TreinamentoInscricao {
  _id: string;
  treinamento_id: string;
  funccodi: number;
  status: InscricaoStatus;
  nota?: number | null;
  certificado_url?: string | null;
  observacoes?: string;
  inscrito_em?: string;
}

// ===== Feedback 360 =====
export type FeedbackCicloStatus = 'rascunho' | 'ativo' | 'encerrado';
export type FeedbackPerguntaTipo = 'escala_1_5' | 'texto_livre' | 'sim_nao';

export interface FeedbackPergunta {
  id: number;
  texto: string;
  tipo: FeedbackPerguntaTipo;
  categoria: string;
}

export interface FeedbackCiclo {
  _id: string;
  empresaId?: string;
  titulo: string;
  descricao?: string;
  data_inicio?: string;
  data_fim?: string;
  status: FeedbackCicloStatus;
  perguntas: FeedbackPergunta[];
  participantes_funccodi: number[];
  criado_em?: string;
}

export interface FeedbackPendencia {
  ciclo_id: string;
  ciclo_titulo: string;
  avaliado_funccodi: number;
  avaliado_nome?: string | null;
  avaliado_cargo?: string | null;
  sou_eu: boolean;
}

export interface FeedbackRelatorio {
  avaliado: { funccodi: number; nome?: string | null; cargo?: string | null };
  ciclo: { id: string; titulo?: string; status?: string };
  total_respostas: number;
  por_categoria: Record<string, number | null>;
  por_pergunta: Array<{
    pergunta_id: number; texto?: string; tipo: FeedbackPerguntaTipo; categoria?: string;
    media?: number | null; distribuicao?: { sim: number; nao: number }; comentarios?: string[];
  }>;
  por_tipo_relacao: Record<string, { media: number | null; total: number }>;
  radar: Array<{ categoria: string; autoavaliacao: number | null; outros: number | null }>;
}

// ===== Vagas =====
export type VagaStatus = 'rascunho' | 'aberta' | 'encerrada' | 'preenchida';
export type VagaEtapa = 'triagem' | 'entrevista_rh' | 'entrevista_gestor' | 'teste' | 'proposta' | 'aprovado' | 'reprovado' | 'desistiu';

export interface Vaga {
  _id: string;
  empresaId?: string;
  titulo: string;
  descricao?: string;
  requisitos?: string;
  beneficios?: string;
  tipo_contrato?: 'clt' | 'pj' | 'estagio' | 'temp';
  modalidade?: 'presencial' | 'remoto' | 'hibrido';
  departamento?: number | string;
  salario_min?: number;
  salario_max?: number;
  salario_combinar?: boolean;
  publicada_em?: string;
  encerra_em?: string;
  status: VagaStatus;
  responsavel_email?: string;
  ativo?: boolean;
}

export interface VagaCandidatoObs {
  autor_email: string;
  em: string;
  texto: string;
}

export interface VagaCandidato {
  _id: string;
  vaga_id: string;
  nome: string;
  email: string;
  telefone?: string;
  cpf_mascarado?: string | null;
  origem?: 'indicacao' | 'site' | 'linkedin' | 'presencial';
  indicado_por_funccodi?: number | null;
  curriculo_url?: string;
  etapa: VagaEtapa;
  observacoes?: VagaCandidatoObs[];
  enviado_em?: string;
  /** Link de DISC pendente (gerado pelo RH, sem login). */
  disc_link?: { link: string; expira_em: string; convite_id: string } | null;
  /** Resultado do DISC respondido pelo link. */
  disc?: { perfil: DiscPerfil; respondido_em: string } | null;
}

// ===== Onboarding (módulo #13) =====
export type OnboardingEtapaTipo = 'tarefa' | 'documento' | 'treinamento' | 'reuniao';
export type OnboardingResponsavelTipo = 'gestor' | 'rh' | 'ti' | 'colaborador';
export type OnboardingEtapaStatus = 'pendente' | 'concluido' | 'atrasado';
export type OnboardingExecucaoStatus = 'em_andamento' | 'concluido' | 'cancelado';

export interface OnboardingEtapaTrilha {
  id: string;
  titulo: string;
  descricao?: string;
  tipo: OnboardingEtapaTipo;
  responsavel_tipo: OnboardingResponsavelTipo;
  dias_apos_admissao: number;
  treinamento_id?: string | null;
}

export interface OnboardingTrilha {
  _id: string;
  empresaId: string;
  nome: string;
  descricao?: string;
  aplicavel_a: { departamentos: string[]; cargos: string[] };
  etapas: OnboardingEtapaTrilha[];
  ativo: boolean;
  criado_em?: string;
}

export interface OnboardingEtapaExec {
  etapa_id: string;
  titulo: string;
  tipo: string;
  dias_apos_admissao: number;
  status: OnboardingEtapaStatus;
  concluido_em?: string | null;
  concluido_por?: string | null;
  observacao?: string | null;
}

export interface OnboardingExecucao {
  _id: string;
  empresaId: string;
  funccodi: number;
  colaborador_nome?: string;
  trilha_id: string;
  trilha_nome: string;
  data_inicio: string;
  status: OnboardingExecucaoStatus;
  progresso_pct: number;
  atrasada: boolean;
  etapas: OnboardingEtapaExec[];
}

// ===== Avaliação de Desempenho + PDI (módulo #11) =====
export type AvaliacaoCompetenciaTipo = 'nota_1_5' | 'texto' | 'escolha';
export type AvaliacaoCicloTipo = '90dias' | '180dias' | '360' | 'anual';
export type AvaliacaoCicloStatus = 'rascunho' | 'ativo' | 'encerrado';
export type TipoAvaliacao = 'auto' | 'gestor' | 'par' | 'liderado';

export interface AvaliacaoCompetencia {
  id: string;
  nome: string;
  descricao?: string;
  peso: number;
  tipo: AvaliacaoCompetenciaTipo;
  opcoes?: string[];
}

export interface AvaliacaoFormulario {
  _id: string;
  empresaId: string;
  nome: string;
  competencias: AvaliacaoCompetencia[];
}

export interface AvaliacaoCiclo {
  _id: string;
  empresaId: string;
  nome: string;
  tipo: AvaliacaoCicloTipo;
  data_inicio?: string;
  data_fim?: string;
  status: AvaliacaoCicloStatus;
  formulario_id: string;
  escopo: { departamentos: string[]; cargos: string[] };
  inclui: { autoavaliacao: boolean; gestor: boolean; pares: boolean; liderados: boolean };
}

export interface AvaliacaoPendente {
  ciclo_id: string;
  ciclo_nome: string;
  formulario_id: string;
  avaliado_funccodi: number;
  avaliado_nome?: string;
  avaliado_cargo?: string;
  tipo_avaliacao: TipoAvaliacao;
}

export interface FormularioParaResponder {
  ciclo: { id: string; nome: string; tipo: string; status: string };
  avaliado: { funccodi: number; nome?: string; cargo?: string };
  tipo_avaliacao: string;
  formulario: AvaliacaoFormulario;
}

export interface RespostaItem {
  competencia_id: string;
  nota?: number;
  comentario?: string;
}

export interface AvaliacaoResultadoRadar {
  competencia: string;
  auto: number | null;
  gestor: number | null;
  pares: number | null;
  liderados: number | null;
}

export interface AvaliacaoResultadoCompetencia {
  competencia_id: string;
  nome: string;
  media: number | null;
  auto: number | null;
  gestor: number | null;
  pares: number | null;
  liderados: number | null;
  anonimato_insuficiente?: boolean;
}

export interface AvaliacaoResultado {
  avaliado: { funccodi: number; nome?: string; cargo?: string };
  ciclo: { id: string; nome: string; status: string };
  total_respostas: number;
  nota_geral: number | null;
  radar: AvaliacaoResultadoRadar[];
  por_competencia: AvaliacaoResultadoCompetencia[];
  por_tipo: { [k: string]: { media: number | null; total: number } };
  comentarios: { competencia?: string; texto: string }[];
}

export type PdiAcaoTipo = 'treinamento' | 'leitura' | 'mentoria' | 'projeto';
export type PdiObjetivoStatus = 'aberto' | 'em_andamento' | 'concluido' | 'cancelado';

export interface PdiAcao {
  id: string;
  tipo: PdiAcaoTipo;
  descricao: string;
  treinamento_id?: string | null;
  concluida_em?: string | null;
}

export interface PdiObjetivo {
  id: string;
  titulo: string;
  descricao?: string;
  prazo?: string;
  status: PdiObjetivoStatus;
  acoes: PdiAcao[];
}

export interface Pdi {
  _id?: string;
  funccodi: number;
  ciclo_origem_id?: string | null;
  objetivos: PdiObjetivo[];
  criado_em?: string;
}

// ===== Kudos / Reconhecimento (módulo #17) =====
export type KudosValor = 'inovacao' | 'cliente' | 'time' | 'excelencia' | 'atitude' | 'entrega';

export interface Kudos {
  _id: string;
  empresaId?: string;
  de_funccodi: number;
  de_nome?: string;
  de_tem_foto?: boolean;
  para_funccodi: number;
  para_nome?: string;
  para_cargo?: string;
  para_tem_foto?: boolean;
  mensagem: string;
  valor: KudosValor;
  curtidas_count: number;
  curti: boolean;
  publico: boolean;
  criado_em?: string;
}

export interface NovoKudos {
  para_funccodi: number;
  mensagem: string;
  valor: KudosValor;
  publico?: boolean;
}

// ===== Aniversariantes / Lembretes (módulo #19) =====
export interface Aniversariante {
  funccodi: number;
  nome?: string;
  cargo?: string;
  centro_custo?: string;
  tem_foto?: boolean;
  dia: number;
  mes: number;
  idade?: number | null;
  anos?: number;
}

export interface MarcoExperiencia {
  funccodi: number;
  nome?: string;
  cargo?: string;
  centro_custo?: string;
  tem_foto?: boolean;
  marco: number;
  dias_de_casa: number;
  data_admissao: string;
}

// ===== Documentos do Colaborador (módulo #16) =====
export type DocumentoTipo =
  | 'aso' | 'nr06' | 'nr10' | 'nr35' | 'contrato' | 'rg' | 'cpf' | 'ctps'
  | 'titulo_eleitor' | 'reservista' | 'comprovante_residencia' | 'diploma'
  | 'certificado' | 'exame_periodico' | 'outro';
export type DocumentoStatus = 'valido' | 'vence_30d' | 'vencido' | 'sem_validade';

export interface DocumentoColab {
  _id: string;
  empresaId?: string;
  funccodi: number;
  colaborador_nome?: string;
  tipo: DocumentoTipo;
  titulo: string;
  arquivo_url?: string | null;
  data_emissao?: string | null;
  data_validade?: string | null;
  observacao?: string | null;
  status: DocumentoStatus;
  dias_para_vencer?: number;
  uploaded_by?: string;
  uploaded_at?: string;
}

export interface NovoDocumento {
  tipo: DocumentoTipo;
  titulo: string;
  arquivo_url?: string;
  data_emissao?: string;
  data_validade?: string;
  observacao?: string;
}

// ===== Sucessão & 9-box (módulo #18) =====
export type FaixaNoveBox = 'baixo' | 'medio' | 'alto';

export interface BoxMeta {
  cod: number;
  nome: string;
  cor: string;
}

export interface PessoaNoveBox {
  funccodi: number;
  nome?: string;
  cargo?: string;
  departamento?: string;
  tem_foto?: boolean;
  desempenho: number | null;
  avaliacoes: number;
  potencial: number;
  faixa_desempenho: FaixaNoveBox | null;
  faixa_potencial: FaixaNoveBox;
  estilo_disc?: string | null;
  anos_casa?: number | null;
  box: BoxMeta | null;
  sem_avaliacao: boolean;
  fatores_potencial?: { fator: string; pontos: number }[];
}

export interface CelulaNoveBox extends BoxMeta {
  desempenho: FaixaNoveBox;
  potencial: FaixaNoveBox;
  pessoas: PessoaNoveBox[];
}

export interface NoveBoxResultado {
  celulas: Record<string, CelulaNoveBox>;
  sem_avaliacao: PessoaNoveBox[];
  total: number;
  avaliados: number;
  departamento?: string | null;
}

export interface Sucessor extends PessoaNoveBox {
  prontidao: number;
  nivel_prontidao: 'pronto' | 'curto_prazo' | 'desenvolvimento';
}

// ===== Offboarding / Desligamento (módulo #20) =====
export type OffboardingMotivo = 'pedido' | 'sem_justa' | 'com_justa' | 'aposentadoria' | 'fim_contrato' | 'acordo';
export type OffboardingIniciativa = 'empresa' | 'colaborador';
export type OffboardingStatus = 'em_andamento' | 'concluido';
export type OffboardingItemStatus = 'pendente' | 'concluido' | 'na';

export interface OffboardingChecklistItem {
  item: string;
  status: OffboardingItemStatus;
  responsavel?: string | null;
}

export interface OffboardingEntrevista {
  realizada: boolean;
  nota_geral?: number | null;
  recomendaria_empresa?: number | null;
  motivos_saida: string[];
  comentarios?: string | null;
}

export interface Offboarding {
  _id: string;
  empresaId?: string;
  funccodi: number;
  colaborador_nome?: string;
  data_desligamento?: string | null;
  motivo: OffboardingMotivo;
  iniciativa: OffboardingIniciativa;
  checklist: OffboardingChecklistItem[];
  entrevista: OffboardingEntrevista;
  status: OffboardingStatus;
  criado_por?: string;
  criado_em?: string;
}

export interface NovoOffboarding {
  funccodi: number;
  data_desligamento?: string;
  motivo: OffboardingMotivo;
  iniciativa: OffboardingIniciativa;
  checklist?: OffboardingChecklistItem[];
  entrevista?: OffboardingEntrevista;
}

export interface OffboardingRelatorio {
  total: number;
  em_andamento: number;
  concluidos: number;
  por_motivo: Record<string, number>;
  por_iniciativa: Record<string, number>;
  entrevistas_realizadas: number;
  enps_saida: number | null;
  motivos_saida: Record<string, number>;
}
